import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SmartVideoVoiceActionService } from '../../VideoGen/services/smart-video-voice-action.service';
import { DriveService } from '../../Drive/services/drive.service';
import { ProcessingAnalysisMode } from '../../Drive/services/client-audio-analyze.service';
import { ProcessingUploadFlowService } from '../../Drive/services/processing-upload-flow.service';
import {
  ProcessingClientRenderPayload,
  ProcessingJobApiService
} from '../../Drive/services/processing-job-api.service';
import { ProcessingRuntimeRenderService } from '../../Drive/services/processing-runtime-render.service';
import { AssistantAction } from '../../../core/services/voice-actions/models';
import { SafeErrorService } from '../../../core/services/safe-error.service';
import { ToastService } from '../../../core/services/toast.service';
import { ReviewEditorCommandRenderService } from '../../Rendering/editor/review-editor-command-render.service';
import { AssetSequenceCommandRunnerService } from '../../Rendering/non-editor/assets/asset-sequence-command-runner.service';
import { TemplateUseCommandRenderService } from '../../Rendering/non-editor/template-use/template-use-command-render.service';
import { EditorProject } from '../../super-editor/core/models/editor-project.model';
import {
  hydrateProject,
  migrateSnapshot
} from '../../super-editor/core/persistence/editor-project-serializer';
import { buildEditorRenderJob } from '../../super-editor/render/editor-project-render-job.adapter';
import { JivuCommand, JivuCommandHandoffFile, JivuCommandHandoffPayload } from './jivu-command.models';
import { JivuCommandProgressReporter } from './jivu-command-progress-reporter.service';
import { JivuCommandService } from './jivu-command.service';

const SMART_VIDEO_COMMAND_ID_KEY = 'jivu-command:active-smart-video-command-id';
const SMART_VIDEO_COMMAND_ID_JOB_SEPARATOR = '::';

type SmartVideoCommandState = {
  commandId: string;
  jobId?: string;
};

type TemplateUseCommandFileRef = {
  label?: string | null;
  role?: string | null;
  url?: string | null;
  filePath?: string | null;
  contentType?: string | null;
  slotId?: string | null;
};

@Injectable({ providedIn: 'root' })
export class JivuCommandExecutorService {
  private readonly router = inject(Router);
  private readonly commandService = inject(JivuCommandService);
  private readonly smartVideoActions = inject(SmartVideoVoiceActionService);
  private readonly driveService = inject(DriveService);
  private readonly processingUploadFlow = inject(ProcessingUploadFlowService);
  private readonly processingApi = inject(ProcessingJobApiService);
  private readonly processingRuntimeRender = inject(ProcessingRuntimeRenderService);
  private readonly injector = inject(Injector);
  private readonly toast = inject(ToastService);
  private readonly safeError = inject(SafeErrorService);
  private readonly progressReporter = inject(JivuCommandProgressReporter);
  private executingCommandId: string | null = null;

  constructor() {
    window.addEventListener('jivu-command:smart-video-completed', (event) => {
      const detail = (event as CustomEvent).detail as { commandId?: string; result?: unknown; message?: string };
      if (!detail?.commandId) return;
      void this.complete(detail.commandId, detail.result, detail.message || 'Smart Video completed on target device.');
    });

    window.addEventListener('jivu-command:smart-video-failed', (event) => {
      const detail = (event as CustomEvent).detail as { commandId?: string; error?: string };
      if (!detail?.commandId) return;
      void this.fail(detail.commandId, detail.error || 'Smart Video failed on target device.');
    });

    window.addEventListener('jivu-command:smart-video-progress', (event) => {
      const detail = (event as CustomEvent).detail as {
        commandId?: string;
        progress?: number;
        message?: string;
        jobId?: string;
      };
      if (!detail?.commandId) return;
      if (detail.jobId) {
        this.setActiveSmartVideoCommandState({ commandId: detail.commandId, jobId: detail.jobId });
      }
      this.progressReporter.report(detail.commandId, detail.progress, detail.message, detail.jobId);
    });
  }

  async execute(command: JivuCommand): Promise<void> {
    if (this.executingCommandId === command.id) return;
    this.executingCommandId = command.id;

    const payload = this.commandService.hydratePayload(command.payload);
    this.commandService.saveCurrentHandoff({ ...command, payload });
    this.toast.show(`Jivu Command claimed: ${command.title || this.labelFor(command.commandType)}`, 'info', 5000);

    try {
      if (command.commandType === 'smart_video_render') {
        await this.startSmartVideo(command, payload);
      } else if (command.commandType === 'processing_upload_render') {
        await this.startProcessingUpload(command, payload);
      } else if (command.commandType === 'template_use_render') {
        await this.startTemplateUseRender(command, payload);
      } else if (command.commandType === 'asset_sequence_render') {
        const assetSequenceRunner = this.injector.get(AssetSequenceCommandRunnerService);
        await assetSequenceRunner.drive(command, payload);
      } else if (command.commandType === 'review_render_or_finalize') {
        const started = await this.startReviewEditorRender(command, payload);
        if (!started) {
          await this.openHandoffPopup(command, payload);
        }
      } else {
        await this.openHandoffPopup(command, payload);
      }
    } catch (error) {
      await this.fail(command.id, this.safeError.message(error, { fallback: 'Command execution failed.' }));
    } finally {
      window.setTimeout(() => {
        if (this.executingCommandId === command.id) {
          this.executingCommandId = null;
        }
      }, 1500);
    }
  }

  private async startReviewEditorRender(
    command: JivuCommand,
    payload: JivuCommandHandoffPayload
  ): Promise<boolean> {
    const metadata = payload.metadata || {};
    const isSuperEditorRender = metadata['superEditorForegroundRender'] === true;
    const action = this.stringValue(metadata['action']);
    const itemType = this.stringValue(metadata['itemType']);
    const reviewItemId = this.reviewItemIdFromPayload(payload);
    const project = this.editorProjectFromPayload(payload);

    if (
      !isSuperEditorRender ||
      action !== 'finalize' ||
      !reviewItemId ||
      !project ||
      (itemType !== 'video' && itemType !== 'shorts')
    ) {
      return false;
    }

    await this.progressReporter.reportNow(
      command.id,
      45,
      'Review render payload loaded on this device.',
      reviewItemId
    );
    await this.progressReporter.reportNow(
      command.id,
      55,
      'Rendering Review timeline on this device.',
      reviewItemId
    );

    const reviewEditorCommandRender = this.injector.get(ReviewEditorCommandRenderService);
    await reviewEditorCommandRender.drive(command.id, project, reviewItemId, {
      showStatusOverlay: false
    });
    return true;
  }

  private async startTemplateUseRender(command: JivuCommand, payload: JivuCommandHandoffPayload): Promise<void> {
    const metadata = payload.metadata || {};
    const postId = this.stringValue(metadata['postId']);
    const script = this.stringValue(metadata['script']);
    const project = this.editorProjectFromPayload(payload);

    if (!postId) {
      throw new Error('Template Use command is missing the Review post target.');
    }
    if (!script) {
      throw new Error('Template Use command is missing the required script.');
    }
    if (!project) {
      throw new Error('Template Use command is missing the editor project snapshot.');
    }

    await this.progressReporter.reportNow(
      command.id,
      35,
      'Loading Template Use render assets on this device.',
      postId
    );
    await this.attachTemplateUseCommandAssets(project, payload);

    const job = buildEditorRenderJob(project);
    if (!job.sources.length) {
      throw new Error('Template Use command has no renderable media assets.');
    }

    await this.progressReporter.reportNow(
      command.id,
      45,
      'Template Use render payload loaded on this device.',
      postId
    );

    const templateUseCommandRender = this.injector.get(TemplateUseCommandRenderService);
    await templateUseCommandRender.drive(command.id, {
      postId,
      job,
      templateId: this.stringValue(metadata['templateId']),
      name: this.stringValue(metadata['title'])
        || this.stringValue(metadata['templateName'])
        || command.title
        || 'Template render',
      durationSeconds: job.timeline.durationSeconds,
      caption: this.stringValue(metadata['caption']) || this.stringValue(metadata['renderDescription']) || undefined,
      script
    });
  }

  private async startProcessingUpload(command: JivuCommand, payload: JivuCommandHandoffPayload): Promise<void> {
    await this.router.navigate(['/review'], {
      queryParams: {
        pipeline: 'Processing',
        commandId: command.id,
        claimed: 'true',
        jivuCommandPanel: payload.handoff.panel
      }
    });
    await this.progressReporter.reportNow(command.id, 10, 'Downloading staged Processing upload on target device.');

    const source = this.sourceFile(payload);
    if (!source) {
      throw new Error('Processing command is missing the staged R2 file.');
    }

    const metadata = payload.metadata || {};
    const fileName = this.stringValue(metadata['fileName']) || source.label || 'jivu-command-upload.mp4';
    const contentType = this.stringValue(metadata['contentType']) || source.contentType || 'video/mp4';
    const directUrl = await this.resolveFileUrl(source);
    const response = await fetch(directUrl);
    if (!response.ok) {
      throw new Error(`Unable to download staged upload (${response.status}).`);
    }

    const blob = await response.blob();
    const file = new File([blob], fileName, { type: contentType });
    const currentFolder = this.stringValue(metadata['currentFolder']) || 'raw';
    const options = metadata['options'] || {};
    const analysisMode = (this.stringValue(metadata['analysisMode']) || 'speech') as ProcessingAnalysisMode;

    await this.progressReporter.reportNow(command.id, 25, 'Preparing Processing flow on target device.');
    let processingJobId = this.processingJobIdFromPayload(payload);
    if (processingJobId) {
      const resumed = await this.tryResumeExistingProcessingRender(command, file, options, processingJobId);
      if (resumed) {
        await this.complete(command.id, {
          commandType: command.commandType,
          fileName,
          currentFolder,
          processingJobId
        }, 'Processing resumed and completed on target device.');
        return;
      }
    }

    await this.processingUploadFlow.upload({
      file,
      currentFolder,
      options,
      analysisMode,
      onJobId: (jobId) => {
        processingJobId = jobId;
        void this.progressReporter.reportNow(command.id, 30, 'Processing job reserved on target device.', jobId);
      },
      onStatus: (message) => {
        this.progressReporter.report(command.id, 55, message, processingJobId || undefined);
      }
    });

    await this.complete(command.id, {
      commandType: command.commandType,
      fileName,
      currentFolder,
      processingJobId
    }, 'Processing upload completed on target device.');
  }

  private async startSmartVideo(command: JivuCommand, payload: JivuCommandHandoffPayload): Promise<void> {
    await this.progressReporter.reportNow(command.id, 15, 'Opening Smart Video on target device.');

    const metadata = payload.metadata || {};
    const resumeJobId = this.smartVideoJobIdFromPayload(payload);
    if (resumeJobId) {
      this.setActiveSmartVideoCommandState({ commandId: command.id, jobId: resumeJobId });
      await this.router.navigate([payload.resume.route || '/video-gen/generator'], {
        queryParams: {
          ...(payload.resume.queryParams || {}),
          commandId: command.id,
          claimed: 'true',
          from: 'jivu-command',
          resumeSmartVideoJobId: resumeJobId,
          resumeSmartVideoTitle: this.stringValue(metadata['title']) || command.title || 'Smart Video'
        }
      });
      await this.progressReporter.reportNow(
        command.id,
        30,
        'Smart Video resume opened on target device.',
        resumeJobId
      );
      return;
    }

    this.setActiveSmartVideoCommandState({ commandId: command.id });
    const draft = {
      ...metadata,
      ...this.objectValue(metadata['runOptions']),
      ...(payload.smartVideoDraft || {})
    };
    const action: AssistantAction = {
      id: 'open_smart_video_create',
      label: command.title || 'Jivu Command Smart Video',
      payload: {
        ...draft,
        source: 'jivu-command'
      }
    };

    await this.smartVideoActions.openCreate(action);
    await this.progressReporter.reportNow(command.id, 30, 'Smart Video opened with saved command payload.');

    if (payload.resume.autoStart) {
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('voice-actions:smart-video:generate'));
      }, 700);
    }
  }

  private async openHandoffPopup(command: JivuCommand, payload: JivuCommandHandoffPayload): Promise<void> {
    await this.progressReporter.reportNow(command.id, 20, 'Opening command handoff popup on target device.');
    await this.router.navigate([payload.resume.route || '/jivu-command'], {
      queryParams: {
        ...(payload.resume.queryParams || {}),
        commandId: command.id,
        panel: payload.handoff.panel,
        claimed: 'true',
        jivuCommandPanel: payload.handoff.panel
      }
    });
    await this.progressReporter.reportNow(command.id, 30, 'Command popup opened with saved R2 URLs and metadata.');
  }

  private async complete(commandId: string, result?: unknown, message?: string): Promise<void> {
    this.clearActiveSmartVideoCommandState();
    this.progressReporter.clear(commandId);
    try {
      await firstValueFrom(this.commandService.complete(commandId, result || {}, message));
      this.commandService.clearCurrentHandoffFor(commandId);
      this.toast.success('Jivu Command completed.');
      this.claimNextQueuedCommandSoon(commandId);
    } catch (error) {
      console.error('Failed to complete Jivu Smart Video command.', error);
      this.toast.error('Smart Video command completion failed.');
    }
  }

  private async fail(commandId: string, error: string): Promise<void> {
    this.clearActiveSmartVideoCommandState();
    this.progressReporter.clear(commandId);
    const safeMessage = this.safeError.message(error, { fallback: 'Smart Video command failed.' });
    try {
      await firstValueFrom(this.commandService.fail(commandId, safeMessage));
      this.commandService.clearCurrentHandoffFor(commandId);
      this.toast.error(safeMessage);
      this.claimNextQueuedCommandSoon(commandId);
    } catch (result) {
      const failureMessage = this.safeError.message(result, { fallback: 'Smart Video command fail request failed.' });
      console.error('Failed to fail Jivu Smart Video command.', result);
      this.toast.error(failureMessage);
    }
  }

  private claimNextQueuedCommandSoon(completedCommandId: string): void {
    this.commandService.acceptQueuedCommandsOnce();
    window.setTimeout(() => {
      firstValueFrom(this.commandService.heartbeatAndPoll())
        .then(response => {
          const next = response.command;
          if (!next || next.id === completedCommandId) return;
          void this.execute(next);
        })
        .catch(error => {
          console.warn('Jivu Command auto-claim after completion failed.', error);
        });
    }, 250);
  }

  private async tryResumeExistingProcessingRender(
    command: JivuCommand,
    file: File,
    options: any,
    processingJobId: string
  ): Promise<boolean> {
    try {
      await this.progressReporter.reportNow(
        command.id,
        35,
        'Checking saved Processing render plan.',
        processingJobId
      );

      const status = await firstValueFrom(this.processingApi.getProcessingJobStatus(processingJobId));
      const clientRender = this.processingClientRenderPayload(status.checkpointResourcesJson, processingJobId);
      if (!clientRender) {
        return false;
      }

      await this.progressReporter.reportNow(
        command.id,
        65,
        'Processing render plan found. Continuing missing Review outputs.',
        processingJobId
      );

      await this.processingRuntimeRender.render({
        file,
        clientRender,
        options,
        fallbackJobId: processingJobId,
        checkpointResourcesJson: status.checkpointResourcesJson ?? null,
        onStatus: (message) => {
          this.progressReporter.report(command.id, 75, message, processingJobId);
        }
      });

      return true;
    } catch (error) {
      console.warn('Processing direct resume failed; falling back to upload flow.', error);
      return false;
    }
  }

  private processingClientRenderPayload(
    checkpointResourcesJson: string | null | undefined,
    processingJobId: string
  ): ProcessingClientRenderPayload | null {
    if (!checkpointResourcesJson?.trim()) {
      return null;
    }

    try {
      const resources = JSON.parse(checkpointResourcesJson) as Record<string, unknown>;
      const raw = resources['ClientRenderPayload'];
      const payload = typeof raw === 'string'
        ? JSON.parse(raw) as ProcessingClientRenderPayload
        : raw && typeof raw === 'object'
          ? raw as ProcessingClientRenderPayload
          : null;

      return payload
        ? { ...payload, jobId: payload.jobId || processingJobId }
        : null;
    } catch {
      return null;
    }
  }

  private labelFor(type: string): string {
    return type.replace(/_/g, ' ');
  }

  private sourceFile(payload: JivuCommandHandoffPayload): JivuCommandHandoffFile | null {
    return payload.r2Files.find(file => file.role === 'source') || payload.r2Files[0] || null;
  }

  private async resolveFileUrl(file: JivuCommandHandoffFile): Promise<string> {
    if (/^https?:\/\//i.test(file.url)) {
      return file.url;
    }

    const direct = await firstValueFrom(this.driveService.getDirectUrl(file.url));
    return direct.url;
  }

  private stringValue(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  private objectValue(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value as Record<string, unknown>
      : {};
  }

  private reviewItemIdFromPayload(payload: JivuCommandHandoffPayload): string | null {
    const metadata = payload.metadata || {};
    const queryParams = payload.resume?.queryParams || {};
    const item = this.objectValue(metadata['item']);
    const candidates = [
      metadata['itemId'],
      item['id'],
      queryParams['itemId'],
      queryParams['previewVideoId']
    ];

    for (const candidate of candidates) {
      const value = this.stringValue(candidate);
      if (value) return value;
    }

    return null;
  }

  private editorProjectFromPayload(payload: JivuCommandHandoffPayload): EditorProject | null {
    const metadata = payload.metadata || {};
    const timeline = this.objectValue(payload.timeline);
    const snapshotRaw = metadata['editorProjectSnapshot'] ?? timeline['editorProjectSnapshot'];
    const snapshot = typeof snapshotRaw === 'string'
      ? this.parseJson(snapshotRaw)
      : snapshotRaw;
    const migrated = migrateSnapshot(snapshot);
    return migrated ? hydrateProject(migrated) : null;
  }

  private async attachTemplateUseCommandAssets(
    project: EditorProject,
    payload: JivuCommandHandoffPayload
  ): Promise<void> {
    const refs = this.templateUseCommandFileRefs(payload);
    if (!refs.length) return;

    await Promise.all(refs.map(ref => this.attachTemplateUseCommandAsset(project, ref)));
  }

  private async attachTemplateUseCommandAsset(project: EditorProject, ref: TemplateUseCommandFileRef): Promise<void> {
    const role = this.stringValue(ref.role);
    const filePath = this.stringValue(ref.filePath) || this.stringValue(ref.url);
    if (!role || !filePath) return;

    const contentType = this.stringValue(ref.contentType);
    const blob = await firstValueFrom(this.driveService.downloadPipelineAssetProxyFile(filePath, contentType));
    const resolvedContentType = blob.type || contentType || undefined;

    if (role.startsWith('template-slot:')) {
      const slotId = this.stringValue(ref.slotId) || role.replace(/^template-slot:/, '').trim();
      if (!slotId) return;
      const matchingAssets = project.assets.filter(asset => this.assetSlotId(asset.metadata) === slotId);
      if (!matchingAssets.length) {
        throw new Error(`Template Use command could not match slot "${slotId}" to the editor project.`);
      }
      for (const asset of matchingAssets) {
        this.attachBlobToAsset(asset, blob, filePath, resolvedContentType);
      }
      return;
    }

    if (role.startsWith('template-audio:')) {
      const audioAsset = project.assets.find(asset =>
        asset.kind === 'audio' && (asset.sourceUrl === filePath || asset.fileKey === filePath)
      ) || project.assets.find(asset => asset.kind === 'audio');
      if (audioAsset) {
        this.attachBlobToAsset(audioAsset, blob, filePath, resolvedContentType);
      }
    }
  }

  private attachBlobToAsset(
    asset: EditorProject['assets'][number],
    blob: Blob,
    filePath: string,
    contentType?: string
  ): void {
    asset.metadata = { ...(asset.metadata ?? {}), blob };
    asset.sourceUrl = filePath;
    asset.fileKey = filePath;
    asset.mimeType = contentType || asset.mimeType;
  }

  private assetSlotId(metadata: Record<string, unknown> | undefined): string | null {
    const value = metadata?.['slotId'];
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  private templateUseCommandFileRefs(payload: JivuCommandHandoffPayload): TemplateUseCommandFileRef[] {
    const metadataSlots = Array.isArray(payload.metadata?.['slots'])
      ? payload.metadata['slots'] as TemplateUseCommandFileRef[]
      : [];
    const r2Refs: TemplateUseCommandFileRef[] = (payload.r2Files || []).map(file => ({
      label: file.label,
      role: file.role,
      url: file.url,
      filePath: file.url,
      contentType: file.contentType
    }));
    const refs = [...r2Refs, ...metadataSlots];
    const unique = new Map<string, TemplateUseCommandFileRef>();

    for (const ref of refs) {
      const role = this.stringValue(ref.role);
      const filePath = this.stringValue(ref.filePath) || this.stringValue(ref.url);
      if (!role || !filePath) continue;
      if (!role.startsWith('template-slot:') && !role.startsWith('template-audio:')) continue;
      unique.set(`${role}|${filePath}`, ref);
    }

    return [...unique.values()];
  }

  private parseJson(value: string): unknown {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private smartVideoJobIdFromPayload(payload: JivuCommandHandoffPayload): string | null {
    const metadata = payload.metadata || {};
    const queryParams = payload.resume?.queryParams || {};
    const candidates = [
      metadata['smartVideoJobId'],
      metadata['jobId'],
      metadata['pipelineJobId'],
      queryParams['resumeSmartVideoJobId'],
      queryParams['smartVideoJobId'],
      queryParams['resumeJobId'],
      queryParams['jobId']
    ];

    for (const candidate of candidates) {
      const value = this.stringValue(candidate);
      if (value) return value;
    }

    return null;
  }

  private processingJobIdFromPayload(payload: JivuCommandHandoffPayload): string | null {
    const metadata = payload.metadata || {};
    const queryParams = payload.resume?.queryParams || {};
    const candidates = [
      metadata['processingJobId'],
      metadata['jobId'],
      metadata['pipelineJobId'],
      queryParams['processingJobId'],
      queryParams['resumeProcessingJobId'],
      queryParams['resumeJobId'],
      queryParams['jobId']
    ];

    for (const candidate of candidates) {
      const value = this.stringValue(candidate);
      if (value) return value;
    }

    return null;
  }

  private setActiveSmartVideoCommandState(state: SmartVideoCommandState): void {
    const serialized = this.serializeSmartVideoCommandState(state);
    if (!serialized) return;
    this.writeLocalStorage(SMART_VIDEO_COMMAND_ID_KEY, serialized);
  }

  private clearActiveSmartVideoCommandState(): void {
    this.removeLocalStorage(SMART_VIDEO_COMMAND_ID_KEY);
  }

  private serializeSmartVideoCommandState(state: SmartVideoCommandState): string {
    const commandId = this.cleanCommandId(state.commandId);
    if (!commandId) return '';
    const cleanedJobId = this.cleanCommandId(state.jobId);
    return cleanedJobId ? `${commandId}${SMART_VIDEO_COMMAND_ID_JOB_SEPARATOR}${cleanedJobId}` : commandId;
  }

  private cleanCommandId(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  private writeLocalStorage(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {}
  }

  private removeLocalStorage(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {}
  }
}
